import classNames from 'classnames'
import React, { useEffect, useMemo, useRef } from 'react'

import { useInternationalization } from '../../context/InternationalizationContext'
import useSizes from '../../hooks/useSizes'
import useWindowDimensions from '../../hooks/useWIndowDimentions'
import { getInstrumentKey } from '../../utils/instrumentsUtils'
import { replaceChordByIdInProg } from '../../utils/progUtils'
import { ViewConfig } from '../../utils/types'
import { useEditMenu } from '../editor/hooks/useEditMenu'
import { usePlayerConfigState } from '../editor/hooks/usePlayerConfigState'
import notification from './Notification/notification'
import styles from './PianoRoll.module.scss'

type PianoSize = 'large' | 'medium' | 'small'

type Props = {
  withLabels?: boolean
  size: PianoSize
  fromOctave?: number
  octaves?: number
  disabledAutoScroll?: boolean
  selectedMidi?: number[]
  playingMidi?: number[]
  webMidi?: number[]
  setPianoScrollDisabled: (v: boolean) => void
}

const NOTE_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B']
const keyTypes = ['whiteLeft', 'whiteCenter', 'whiteRight', 'black'] as const
type KeyType = typeof keyTypes[number]
const getKeyTypeByPosition = (pos: number): KeyType => {
  return {
    0: 'whiteLeft',
    1: 'black',
    2: 'whiteCenter',
    3: 'black',
    4: 'whiteRight',
    5: 'whiteLeft',
    6: 'black',
    7: 'whiteCenter',
    8: 'black',
    9: 'whiteCenter',
    10: 'black',
    11: 'whiteRight',
  }[pos] as KeyType
}
const SVG_MEDIUM_BY_KEY_TYPE = {
  whiteCenter:
    'M6.5326 84.8003h22.4651s6.4186 0 6.4186 6.4186v22.3628s0 6.4186 -6.4186 6.4186h-22.4651s-6.4186 0 -6.4186 -6.4186v-22.3628s0 -6.4186 6.4186 -6.4186',
  whiteLeft:
    'M6.4 0C2.86538 0 0 2.86538 0 6.4V113.6C0 117.135 2.86538 120 6.4 120H28.9C32.4346 120 35.3 117.135 35.3 113.6V91.2C35.3 87.6654 32.4346 84.8 28.9 84.8H26.4432C20.2576 84.8 15.2432 79.7856 15.2432 73.6V6.39999C15.2432 2.86537 12.3778 0 8.84317 0H6.4Z',
  whiteRight:
    'M28.8836 0C32.4285 0 35.3022 2.87371 35.3022 6.4186V113.581C35.3022 117.126 32.4285 120 28.8836 120H6.41853C2.87363 120 -8.01086e-05 117.126 -8.01086e-05 113.581V91.2186C-8.01086e-05 87.6737 2.87363 84.8 6.41853 84.8H8.82549C15.0291 84.8 20.0581 79.771 20.0581 73.5674V6.4186C20.0581 2.87371 22.9318 0 26.4767 0H28.8836Z',
  black:
    'M5.673 0.1162h25.0027s5.1498 0 5.1498 5.1498v69.7004s0 5.1498 -5.1498 5.1498h-25.0027s-5.1498 0 -5.1498 -5.1498v-69.7004s0 -5.1498 5.1498 -5.1498',
}

const SVG_LARGE_PATH_BY_KEY_TYPE = {
  whiteCenter: 'M8 106h28s8 0 8 8v28s0 8 -8 8h-28s-8 0 -8 -8v-28s0 -8 8 -8',
  whiteLeft:
    'M8 0C3.58172 0 0 3.58172 0 8V142C0 146.418 3.58172 150 8 150H36C40.4183 150 44 146.418 44 142V114C44 109.582 40.4183 106 36 106H33C25.268 106 19 99.732 19 92V8C19 3.58172 15.4183 0 11 0H8Z',
  whiteRight:
    'M36 0C40.4183 0 44 3.58172 44 8V142C44 146.418 40.4183 150 36 150H8C3.58172 150 0 146.418 0 142V114C0 109.582 3.58172 106 8 106H11C18.732 106 25 99.732 25 92V8C25 3.58172 28.5817 0 33 0H36Z',
  black: 'M8 0h28s8 0 8 8v84s0 8 -8 8h-28s-8 0 -8 -8v-84s0 -8 8 -8',
}

const SVG_SMALL_PATH_BY_KEY_TYPE = {
  whiteCenter: 'M4 56.1001h14s4 0 4 4v20.9s0 4 -4 4h-14s-4 0 -4 -4v-20.9s0 -4 4 -4',
  whiteLeft:
    'M4 0C1.79086 0 0 1.79086 0 4V81C0 83.2091 1.79086 85 4 85H18C20.2091 85 22 83.2091 22 81V60.1115C22 57.896 20.204 56.1 17.9885 56.1H16.0001C12.6864 56.1 10.0001 53.4137 10.0001 50.1V4.01153C10.0001 1.79602 8.20407 0 5.98857 0H4Z',
  whiteRight:
    'M18 0C20.2091 0 22 1.79086 22 4V81C22 83.2091 20.2091 85 18 85H4C1.79086 85 0 83.2091 0 81V60.1115C0 57.896 1.79602 56.1 4.01153 56.1H5.99993C9.31364 56.1 11.9999 53.4137 11.9999 50.1V4.01153C11.9999 1.79602 13.7959 0 16.0115 0H18Z',
  black:
    'M4.0115 0h13.9769s4.0115 0 4.0115 4.0115v45.9769s0 4.0115 -4.0115 4.0115h-13.9769s-4.0115 0 -4.0115 -4.0115v-45.9769s0 -4.0115 4.0115 -4.0115',
}

const getKeyWidth = (size: PianoSize) => {
  switch (size) {
    case 'small':
      return 22
    case 'medium':
      return 36
    case 'large':
      return 44
  }
}
const getInstrumentViewHeight = (size: PianoSize, view: ViewConfig) => {
  const layersHeight = 0
  // TODO: uncomment to return 571 voicings
  // const layersHeight = view.layersOpen === INSTRUMENT_TYPES.CHORDS ? 40 : 0

  switch (size) {
    case 'small':
      return 95 + layersHeight
    case 'medium':
      return 120 + layersHeight
    case 'large':
      return 150 + layersHeight
  }
}
const getKeyPadding = (size: PianoSize) => {
  switch (size) {
    case 'small':
      return 2
    case 'medium':
      return 4
    case 'large':
      return 6
  }
}
const getSVGConfig = (size: PianoSize) => {
  switch (size) {
    case 'small':
      return SVG_SMALL_PATH_BY_KEY_TYPE
    case 'medium':
      return SVG_MEDIUM_BY_KEY_TYPE
    case 'large':
      return SVG_LARGE_PATH_BY_KEY_TYPE
  }
}

const OFFSETS = [0, 0.5, 1, 1.5, 2, 3, 3.5, 4, 4.5, 5, 5.5, 6]

const PianoRoll: React.FC<Props> = ({
  withLabels,
  size,
  fromOctave,
  octaves,
  disabledAutoScroll,
  selectedMidi,
  playingMidi,
  webMidi,
  setPianoScrollDisabled,
}) => {
  const { text } = useInternationalization()
  const { isMobile } = useSizes()
  const { width } = useWindowDimensions()
  const {
    playerConfig: { view, prog, activeChord, currentPart, currentPartId, player },
  } = usePlayerConfigState()
  const { editingVoicing, setEditingVoicing } = useEditMenu()

  const scrollRef = useRef<HTMLDivElement>(null)

  const allowedMidi = useMemo(() => editingVoicing?.defaultMidi || [], [editingVoicing?.name])

  const keysArray = Array.from(Array(octaves! * 12).keys())
  const keyWidth = getKeyWidth(size)
  const keyPadding = getKeyPadding(size)
  const rollHeight = getInstrumentViewHeight(size, view)
  const SVG_CONFIG = getSVGConfig(size)
  const showVoicings = false
  const HEIGHT = rollHeight - (showVoicings ? 40 : 0)

  const octaveLengthPx = (keyWidth + keyPadding) * 7
  const fromMidi = fromOctave! * 12 + 24

  const STRINGS = text.footer?.pianoRoll

  useEffect(() => {
    const offset = getActiveChordPosition()
    if (offset === null) {
      return
    }
    scrollRef.current?.scrollBy({
      left: offset - scrollRef.current?.scrollLeft,
      behavior: 'smooth',
    })
  }, [selectedMidi, playingMidi])

  const getActiveChordPosition = () => {
    const allMidi = (playingMidi?.length ? playingMidi : selectedMidi) || []
    if (!allMidi.length || disabledAutoScroll) {
      return null
    }
    if (isMobile) {
      const midi = allMidi[0]
      return getKeyLeft(midi)
    }
    const first = allMidi[0]
    const last = allMidi[allMidi.length - 1]
    const center = (getKeyLeft(last) + getKeyLeft(first)) / 2
    return Math.max(0, center - width! / 2)
  }
  const getKeyLeft = (midi: number) => {
    const octavePx = Math.floor((midi - fromMidi) / 12) * octaveLengthPx
    const keyPx = (OFFSETS[midi % 12] / 7) * octaveLengthPx

    return octavePx + keyPx
  }
  const GetKey = (midi: number) => {
    const isVoicingEditMode = !!editingVoicing
    const isAvailableForVoicing = isVoicingEditMode && allowedMidi.some((m) => m % 12 === midi % 12)

    const selectedInVoicing = isAvailableForVoicing && editingVoicing.midi.includes(midi)
    const selected = isVoicingEditMode ? selectedInVoicing : selectedMidi?.includes(midi)
    const playing = playingMidi?.includes(midi)
    const webMidiPlaying = webMidi?.includes(midi)

    const keyType = getKeyTypeByPosition(midi % 12)

    const isBlack = keyType === 'black'
    const isActive = playing || webMidiPlaying
    const isSelected = selected || isActive

    const activeLinearGradientId = `piano-roll-gradient-${midi}-active`
    const selectedLinearGradientId = `piano-roll-gradient-${midi}-selected`

    const getMidiColor = () => {
      if (isActive) return `url(#${activeLinearGradientId})`
      if (selected) return `url(#${selectedLinearGradientId})`
      return undefined
    }
    const handlePlayMidi = () => {
      const instrumentKeys = currentPart.chordLayers.map((layer, index) =>
        getInstrumentKey(layer.instrument, index, currentPartId),
      )

      if (isAvailableForVoicing) {
        if (editingVoicing.midi.length <= 3 && isSelected)
          return notification.error(STRINGS.chord_cant_have_less_than_3)
        if (editingVoicing.midi.length >= 10 && !isSelected)
          return notification.error(STRINGS.chord_cant_have_more_than_10)

        const newMidi = isSelected ? editingVoicing.midi.filter((m) => m !== midi) : [...editingVoicing.midi, midi]

        if (activeChord && prog) {
          player.playChord(
            replaceChordByIdInProg(activeChord.id, prog, { ...activeChord, midi: newMidi }, currentPartId),
            activeChord.id,
          )
        }

        setEditingVoicing({ ...editingVoicing, midi: newMidi })
      } else {
        player.playMidi!(midi, instrumentKeys)
      }
    }
    const keyHeight = isBlack ? HEIGHT * 0.66 : HEIGHT
    const pathD = SVG_CONFIG[getKeyTypeByPosition(midi % 12)]
    return (
      <svg
        viewBox={`0 0 ${keyWidth} ${keyHeight}`}
        key={midi}
        className={classNames(
          styles[keyType + 'Key'],
          { [styles.selected]: isSelected },
          { [styles.active]: isActive },
          { [styles.availableForVoicing]: isAvailableForVoicing },
        )}
        style={{
          left: getKeyLeft(midi),
          width: keyWidth,
        }}
      >
        <path onClick={handlePlayMidi} d={pathD} fill={getMidiColor()} />
        <path
          onClick={handlePlayMidi}
          className={styles.stroke}
          fill={getMidiColor()}
          d={pathD}
          transform={`scale(${(keyWidth - 3) / keyWidth}, ${(keyHeight - 3) / keyHeight})`}
        />
        {!isMobile && (midi % 12 === 0 || isSelected) ? (
          <text
            onClick={handlePlayMidi}
            x='50%'
            y={(isBlack ? HEIGHT / 3 : (HEIGHT / 6) * 5) + 6}
            dominantBaseline='middle'
            textAnchor='middle'
            className={styles.keyLabel}
          >
            {getLabel(midi)}
          </text>
        ) : null}
        <linearGradient
          id={selectedLinearGradientId}
          x1='13.2'
          y1='56.8421'
          x2='56.9724'
          y2='64.6538'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor={'#5A7FFF'} />
          <stop offset='1' stopColor={'#7BB8FF'} />
        </linearGradient>
        <linearGradient
          id={activeLinearGradientId}
          x1='13.2'
          y1='56.8421'
          x2='56.9724'
          y2='64.6538'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor={'#CE9C0C'} />
          <stop offset='1' stopColor={'#F3C12F'} />
        </linearGradient>
      </svg>
    )
  }
  const getLabel = (midi: number) => {
    return `${NOTE_NAMES[midi % 12]}${fromOctave! + Math.floor((midi - fromMidi) / 12)}`
  }

  return (
    <div className={styles.container} style={{ height: rollHeight }} ref={scrollRef}>
      <div
        style={{ width: octaveLengthPx * octaves! }}
        className={classNames(styles.scroll, { [styles.withLabels]: withLabels })}
      >
        <div className={styles.piano}>
          {keysArray.map((index) => {
            const midi = index + fromMidi
            const keyType = getKeyTypeByPosition(midi % 12)
            if (keyType === 'black') {
              // black keys should be rendered above white ones
              return null
            }
            return GetKey(midi)
          })}
          {keysArray.map((index) => {
            const midi = index + fromMidi
            const keyType = getKeyTypeByPosition(midi % 12)
            if (keyType !== 'black') {
              return null
            }
            return GetKey(midi)
          })}
        </div>
      </div>
    </div>
  )
}

PianoRoll.defaultProps = {
  withLabels: true,
  size: 'medium',
  fromOctave: 1,
  octaves: 6,
  selectedMidi: [],
}

export default PianoRoll
